import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from "recharts";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Target,
  TrendingUp,
  TrendingDown,
  Activity,
} from "lucide-react";
import { format, eachDayOfInterval, eachWeekOfInterval } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface QualityReportProps {
  dateRange: {
    start: Date;
    end: Date;
  };
}

export function QualityReport({ dateRange }: QualityReportProps) {
  const { data: inspections } = useQuery({
    queryKey: ["quality-report", dateRange.start, dateRange.end],
    queryFn: async () => {
      const { data, error } = await api
        .from("quality_inspections")
        .select(`
          *,
          work_orders(order_number, product_name),
          profiles:inspector_id(full_name, email)
        `)
        .gte("inspection_date", dateRange.start.toISOString())
        .lte("inspection_date", dateRange.end.toISOString())
        .order("inspection_date", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: defects } = useQuery({
    queryKey: ["defects-report", dateRange.start, dateRange.end],
    queryFn: async () => {
      const { data, error } = await api
        .from("quality_defects")
        .select("*")
        .gte("created_at", dateRange.start.toISOString())
        .lte("created_at", dateRange.end.toISOString());
      if (error) throw error;
      return data;
    },
  });

  // Calculate metrics
  const totalInspections = inspections?.length || 0;
  const passedInspections = inspections?.filter((i) => i.status === "passed").length || 0;
  const failedInspections = inspections?.filter((i) => i.status === "failed").length || 0;
  const conditionalInspections = inspections?.filter((i) => i.status === "conditional").length || 0;
  const pendingInspections = inspections?.filter((i) => i.status === "pending" || i.status === "in_progress").length || 0;

  const passRate = totalInspections > 0
    ? (passedInspections / (passedInspections + failedInspections)) * 100
    : 0;

  const totalDefects = defects?.length || 0;
  const defectsPerInspection = totalInspections > 0 ? totalDefects / totalInspections : 0;

  // Status distribution
  const statusData = [
    { name: "Aprobadas", value: passedInspections, color: "#22c55e" },
    { name: "Rechazadas", value: failedInspections, color: "#ef4444" },
    { name: "Condicionales", value: conditionalInspections, color: "#eab308" },
    { name: "Pendientes", value: pendingInspections, color: "#6b7280" },
  ].filter((d) => d.value > 0);

  // Defects by severity
  const defectsBySeverity = {
    critical: defects?.filter((d) => d.severity === "critical").length || 0,
    major: defects?.filter((d) => d.severity === "major").length || 0,
    minor: defects?.filter((d) => d.severity === "minor").length || 0,
    cosmetic: defects?.filter((d) => d.severity === "cosmetic").length || 0,
  };

  const severityData = [
    { name: "Criticos", value: defectsBySeverity.critical, color: "#ef4444" },
    { name: "Mayores", value: defectsBySeverity.major, color: "#f97316" },
    { name: "Menores", value: defectsBySeverity.minor, color: "#eab308" },
    { name: "Cosmeticos", value: defectsBySeverity.cosmetic, color: "#3b82f6" },
  ];

  // Defects by type
  const defectsByType = defects?.reduce((acc: Record<string, number>, d) => {
    const type = d.defect_type || "Sin tipo";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {}) || {};

  const defectTypeData = Object.entries(defectsByType)
    .map(([type, count]) => ({ type, cantidad: count }))
    .sort((a, b) => b.cantidad - a.cantidad)
    .slice(0, 8);

  // Inspections by type
  const inspectionsByType = {
    incoming: inspections?.filter((i) => i.inspection_type === "incoming").length || 0,
    in_process: inspections?.filter((i) => i.inspection_type === "in_process").length || 0,
    final: inspections?.filter((i) => i.inspection_type === "final").length || 0,
    random: inspections?.filter((i) => i.inspection_type === "random").length || 0,
  };

  // Quality trend by date
  const daysDiff = Math.ceil(
    (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
  );
  const useWeekly = daysDiff > 30;

  const intervals = useWeekly
    ? eachWeekOfInterval({ start: dateRange.start, end: dateRange.end }, { weekStartsOn: 1 })
    : eachDayOfInterval({ start: dateRange.start, end: dateRange.end });

  const trendData = intervals.map((date) => {
    const nextDate = useWeekly
      ? new Date(date.getTime() + 7 * 24 * 60 * 60 * 1000)
      : new Date(date.getTime() + 24 * 60 * 60 * 1000);

    const periodInspections = inspections?.filter((i) => {
      const inspDate = new Date(i.inspection_date);
      return inspDate >= date && inspDate < nextDate;
    }) || [];

    const passed = periodInspections.filter((i) => i.status === "passed").length;
    const failed = periodInspections.filter((i) => i.status === "failed").length;
    const rate = (passed + failed) > 0 ? (passed / (passed + failed)) * 100 : 0;

    return {
      date: useWeekly
        ? `Sem ${format(date, "w", { locale: es })}`
        : format(date, "dd/MM", { locale: es }),
      tasa: Number(rate.toFixed(1)),
      inspecciones: periodInspections.length,
    };
  });

  // Top inspectors
  const inspectorStats = inspections?.reduce((acc: Record<string, { total: number; passed: number }>, i) => {
    const inspector = i.profiles?.full_name || i.profiles?.email || "Sin asignar";
    if (!acc[inspector]) {
      acc[inspector] = { total: 0, passed: 0 };
    }
    acc[inspector].total++;
    if (i.status === "passed") acc[inspector].passed++;
    return acc;
  }, {}) || {};

  const inspectorData = Object.entries(inspectorStats)
    .map(([name, stats]) => ({
      name: name.substring(0, 15),
      inspecciones: stats.total,
      tasa: stats.total > 0 ? (stats.passed / stats.total) * 100 : 0,
    }))
    .sort((a, b) => b.inspecciones - a.inspecciones)
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Activity className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalInspections}</p>
                <p className="text-sm text-muted-foreground">Inspecciones</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{passedInspections}</p>
                <p className="text-sm text-muted-foreground">Aprobadas</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{failedInspections}</p>
                <p className="text-sm text-muted-foreground">Rechazadas</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={cn(
                "p-2 rounded-lg",
                passRate >= 95 ? "bg-green-500/10" : passRate >= 85 ? "bg-yellow-500/10" : "bg-red-500/10"
              )}>
                <Target className={cn(
                  "w-5 h-5",
                  passRate >= 95 ? "text-green-500" : passRate >= 85 ? "text-yellow-500" : "text-red-500"
                )} />
              </div>
              <div>
                <p className="text-2xl font-bold">{passRate.toFixed(1)}%</p>
                <p className="text-sm text-muted-foreground">Tasa Aprobacion</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalDefects}</p>
                <p className="text-sm text-muted-foreground">Defectos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Estado de Inspecciones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Defects by Severity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Defectos por Severidad</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {severityData.map((severity) => (
                <div key={severity.name} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: severity.color }}
                      />
                      <span>{severity.name}</span>
                    </div>
                    <span className="font-bold">{severity.value}</span>
                  </div>
                  <Progress
                    value={(severity.value / (totalDefects || 1)) * 100}
                    className="h-2"
                  />
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between text-sm">
                <span>Defectos por inspeccion</span>
                <span className="font-bold">{defectsPerInspection.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quality Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Tendencia de Calidad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis
                  yAxisId="left"
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--background))",
                    border: "1px solid hsl(var(--border))",
                  }}
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="tasa"
                  name="Tasa Aprobacion (%)"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="inspecciones"
                  name="Inspecciones"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Defects by Type */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tipos de Defectos Mas Frecuentes</CardTitle>
          </CardHeader>
          <CardContent>
            {defectTypeData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={defectTypeData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="type" type="category" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="cantidad" fill="#ef4444" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p>No hay defectos registrados</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Inspections by Type */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Inspecciones por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-blue-500/10 text-center">
                <p className="text-3xl font-bold text-blue-600">{inspectionsByType.incoming}</p>
                <p className="text-sm text-muted-foreground">Recepcion</p>
              </div>
              <div className="p-4 rounded-lg bg-purple-500/10 text-center">
                <p className="text-3xl font-bold text-purple-600">{inspectionsByType.in_process}</p>
                <p className="text-sm text-muted-foreground">En Proceso</p>
              </div>
              <div className="p-4 rounded-lg bg-green-500/10 text-center">
                <p className="text-3xl font-bold text-green-600">{inspectionsByType.final}</p>
                <p className="text-sm text-muted-foreground">Final</p>
              </div>
              <div className="p-4 rounded-lg bg-orange-500/10 text-center">
                <p className="text-3xl font-bold text-orange-600">{inspectionsByType.random}</p>
                <p className="text-sm text-muted-foreground">Aleatoria</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Inspectors */}
      {inspectorData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Rendimiento por Inspector</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={inspectorData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--background))",
                      border: "1px solid hsl(var(--border))",
                    }}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="inspecciones" name="Inspecciones" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="tasa" name="Tasa Aprobacion %" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
